import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import isEmpty from 'lodash/isEmpty';
import API from 'services/API';
import { UIStatus } from 'UI/constants/status';
import { getErrorMessage } from 'UI/utils';

export const useInterviewTabData = ({ endpoint }) => {
  const resolvedEndpoint = endpoint ?? null;
  const latestRequestIdRef = useRef(0);
  const [state, setState] = useState({
    status: resolvedEndpoint ? UIStatus.Loading : UIStatus.Default,
    results: null,
    error: null,
    responseStatusCode: null
  });

  const refreshData = useCallback(
    async signal => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      if (!resolvedEndpoint) {
        setState({
          status: UIStatus.Default,
          results: null,
          error: null,
          responseStatusCode: null
        });
        return;
      }

      setState({
        status: UIStatus.Loading,
        results: null,
        error: null,
        responseStatusCode: null
      });

      try {
        const response = await API.get(resolvedEndpoint, { apiVersion: 2, signal });

        if (requestId !== latestRequestIdRef.current) return;

        const results = response?.data ?? null;

        setState({
          status: UIStatus.Success,
          results: isEmpty(results) ? null : results,
          error: null,
          responseStatusCode: null
        });
      } catch (error) {
        if (axios.isCancel(error)) return;
        if (requestId !== latestRequestIdRef.current) return;

        const responseStatusCode = error?.response?.status ?? null;

        if (responseStatusCode === HTTPStatusCodes.NotFound) {
          setState({
            status: UIStatus.Success,
            results: null,
            error: null,
            responseStatusCode
          });
          return;
        }

        setState({
          status: UIStatus.Error,
          results: null,
          error: getErrorMessage(error),
          responseStatusCode
        });
      }
    },
    [resolvedEndpoint]
  );

  useEffect(() => {
    const controller = new AbortController();

    refreshData(controller.signal);

    return () => controller.abort();
  }, [refreshData]);

  return { state };
};
